import React from 'react';
import { Grid, Typography, createStyles, makeStyles } from '@material-ui/core';
import { useTranslation } from '../../../../hooks/useTranslation';
import Modal from '../../../modal';
import Button from '../../../buttons';
import colors from '../../../../themes/main-colors';
import theme from '../../../../themes/theme';
import { navigate } from 'gatsby';
import ROUTES from '../../../../routes';

export type EligibilityInfo = {
  isEligible: boolean;
  spTypeNotResidential: boolean;
  pendingDisconnect: boolean;
  meterStatusInactive: boolean;
};

type PremiseEligibilityProps = {
  isOpen: boolean;
  handleClose: () => void;
  userAddress: string;
  eligibilityInfo: EligibilityInfo;
};

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: '32px',
      color: colors.noirBlur,
      marginBottom: '6px',
    },
    container: {
      maxWidth: '680px',
    },
    descriptionText: {
      fontSize: '20px',
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
      },
    },
    body: {
      backgroundColor: colors.modalBackgroundColor,
      marginTop: '20px',
      padding: '10px 25px 10px 25px',
      borderRadius: '4px',
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
    },
    bodyText: {
      fontSize: '18px',
      color: colors.noirBlur,
      marginTop: '10px',
    },
    marginBottom: {
      marginBottom: '6px',
    },
    actions: {
      display: 'flex',
      justifyContent: 'end',
      marginTop: '20px',
    },
    back: {
      fontSize: '18px',
      padding: '5px 25px 5px 25px',
      [theme.breakpoints.down('sm')]: {
        width: '45%',
      },
    },
    okay: {
      fontSize: '18px',
      color: colors.white,
      padding: '5px 25px 5px 25px',
      marginLeft: '20px',
      [theme.breakpoints.down('sm')]: {
        width: '45%',
        marginLeft: '10px',
      },
    },
  }),
);

export const PremiseEligibility = (props: PremiseEligibilityProps) => {
  const { isOpen, handleClose, userAddress, eligibilityInfo } = props;
  const { t, inlineRichT } = useTranslation();
  const classes = useStyles();
  const textData: {
    titleText: string;
    descriptionText: string;
    bodyText1: React.ReactNode;
    bodyText2: React.ReactNode;
  } = {
    titleText: '',
    descriptionText: '',
    bodyText1: <></>,
    bodyText2: <></>,
  };
  if (eligibilityInfo.spTypeNotResidential) {
    textData.titleText = t('COMMERCIAL_BUILDING_1');
    textData.descriptionText = t('UNABLE_TO_START_BUSINESS_SERVICE_ONLINE');
    textData.bodyText1 = inlineRichT('ADDRESS_ENTERED_1', { userAddress });
    textData.bodyText2 = inlineRichT('COMMERCIAL_ADDRESS_ERROR_2', {
      BUSINESS_CUSTOMER_SERVICE_NUMBER: t('BUSINESS_CUSTOMER_SERVICE_NUMBER'),
    });
  } else if (eligibilityInfo.pendingDisconnect) {
    textData.titleText = t('PENDING_DISCONNECT');
    textData.descriptionText = t('WE_CANNOT_COMPLETE_ONLINE_REQUEST') + '.';
    textData.bodyText1 = inlineRichT('ADDRESS_ENTERED_1', { userAddress });
    textData.bodyText2 = inlineRichT('LOCATION_DISCONNECT_ERROR', {
      CALL_FOR_ASSISTANCE_NUMBER: t('CALL_FOR_ASSISTANCE_NUMBER'),
    });
  } else if (eligibilityInfo.meterStatusInactive) {
    textData.titleText = t('INACTIVE_METER');
    textData.descriptionText = t('WE_CANNOT_COMPLETE_ONLINE_REQUEST') + '.';
    textData.bodyText1 = inlineRichT('INACTIVE_METER_ERROR_1', { userAddress });
    textData.bodyText2 = inlineRichT('INACTIVE_METER_CALL_PGE', {
      CUSTOMER_CARE_NUMBER_OTHER: t('CUSTOMER_CARE_NUMBER_OTHER'),
    });
  }

  return (
    <Modal
      open={isOpen}
      showCloseIcon={true}
      onClose={handleClose}
      title={
        <Typography
          variant="body1"
          className={classes.title}
          data-testid="modal-title"
        >
          {textData.titleText}
        </Typography>
      }
    >
      <Grid container className={classes.container}>
        <Grid container>
          <Typography
            className={classes.descriptionText}
            data-testid="modal-description-text"
          >
            {textData.descriptionText}
          </Typography>
        </Grid>
        <Grid container className={classes.body}>
          <Typography
            className={classes.bodyText}
            data-testid="modal-body-text-1"
          >
            {textData.bodyText1}
          </Typography>
          <Typography
            className={`${classes.bodyText} ${classes.marginBottom}`}
            data-testid="modal-body-text-2"
          >
            {textData.bodyText2}
          </Typography>
        </Grid>
        <Grid container className={classes.actions}>
          <Button
            color={'secondary'}
            variant={'outlined'}
            className={classes.back}
            onClick={handleClose}
            data-testid="modal-back-button"
          >
            {t('BACK')}
          </Button>
          <Button
            color={'primary'}
            variant={'contained'}
            className={classes.okay}
            onClick={() => navigate(ROUTES.HOME)}
            data-testid="modal-okay-button"
          >
            {t('OK')}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};
